// Shared formatting utilities

// Markdown rendering comes from d3-kit: a dependency-free, sanitizing renderer
// (input is HTML-escaped before any construct applies), so there's no `marked`
// and no unsafeHTML injection sink in this app.
export { renderMarkdown } from '@dice-driven/d3-kit';

/**
 * Convert a slug like "some-thing" to title case "Some Thing"
 * @param {string} slug
 * @returns {string}
 */
export function slugToTitle(slug) {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * @param {{selectionType?: string}} choice
 * @returns {string}
 */
export function getSelectionType(choice) {
  return choice.selectionType || 'single';
}

/**
 * @param {{choiceType?: string, options?: any}} choice
 * @returns {boolean}
 */
export function isExpertiseChoice(choice) {
  return choice.choiceType === 'skill' && !choice.options;
}

/**
 * @param {{choiceType?: string, id?: string}} choice
 * @returns {boolean}
 */
export function isSpellListChoice(choice) {
  return choice.choiceType === 'option' && choice.id === 'spellList';
}

/**
 * @param {{label?: string, name?: string, id?: string}} choice
 * @returns {string}
 */
export function getChoiceLabel(choice) {
  return choice.label || choice.name || choice.id || '';
}

/**
 * Parse a cost string to copper pieces
 * @param {string} cost
 * @returns {number}
 */
export function parseCostToCopper(cost) {
  const match = cost.toLowerCase().match(/^([\d.]+)\s*(cp|sp|ep|gp|pp)$/);
  if (!match) return 0;
  const [, amount, unit] = match;
  const value = parseFloat(/** @type {string} */ (amount));
  switch (unit) {
    case 'cp':
      return Math.round(value);
    case 'sp':
      return Math.round(value * 10);
    case 'ep':
      return Math.round(value * 50);
    case 'gp':
      return Math.round(value * 100);
    case 'pp':
      return Math.round(value * 1000);
    default:
      return 0;
  }
}

/**
 * Format copper pieces as a readable currency string
 * @param {number} copperPieces
 * @returns {string}
 */
export function formatCurrency(copperPieces) {
  if (copperPieces <= 0) return '0 gp';
  if (copperPieces >= 100) {
    const gp = Math.floor(copperPieces / 100);
    const remainder = copperPieces % 100;
    if (remainder === 0) return `${gp} gp`;
    const sp = Math.floor(remainder / 10);
    const cp = remainder % 10;
    let result = `${gp} gp`;
    if (sp > 0) result += `, ${sp} sp`;
    if (cp > 0) result += `, ${cp} cp`;
    return result;
  }
  if (copperPieces >= 10) {
    const sp = Math.floor(copperPieces / 10);
    const cp = copperPieces % 10;
    if (cp === 0) return `${sp} sp`;
    return `${sp} sp, ${cp} cp`;
  }
  return `${copperPieces} cp`;
}

/** @typedef {import('../types/jsdoc-types.js').ChoiceOption} ChoiceOption */
/** @typedef {import('../types/jsdoc-types.js').FeatureChoice} FeatureChoice */

/**
 * Detect if a choice references origin feats
 * @param {FeatureChoice} choice
 * @returns {boolean}
 */
export function isOriginFeatChoice(choice) {
  if (choice.options === '{rules/feats/origin}' || choice.choiceType === 'feat') return true;
  return (
    Array.isArray(choice.options) &&
    choice.options.some(
      (/** @type {ChoiceOption | string} */ o) =>
        typeof o !== 'string' && (o.slug === '{rules/feats/origin}' || o.reference === 'rules/feats/origin'),
    )
  );
}
